/**
 * Key behaviours (scoring components) for a threat area, from LMS catalog
 * (`catalogStore.threatAreaServerData` / threatAreas list API).
 */

/** Display: `[code] human-readable text` (stored `value` stays lowercase code). */
export function formatScoringOptionLabel(code, text) {
    const c = String(code ?? '').trim() || 'behaviour';
    const t = String(text ?? '').trim() || c;
    return `[${c}] ${t}`;
}

const DEFAULT_SCORING_OPTIONS = [
    { value: 'behaviour', label: formatScoringOptionLabel('behaviour', 'Behaviour') },
    { value: 'knowledge', label: formatScoringOptionLabel('knowledge', 'Knowledge') },
];

/**
 * @param {unknown[]} catalog - threat area records with `threatAreaName`, `keyBehaviours[]`
 * @param {string} [areaName] - question.area (display name, e.g. "Password handling")
 * @returns {{ kbCode: string, kbName: string }[]}
 */
export function keyBehavioursForThreatAreaName(catalog, areaName) {
    if (!areaName || !Array.isArray(catalog)) return [];
    const name = String(areaName).trim();
    const ta = catalog.find(
        (x) => String(x?.threatAreaName ?? '').trim() === name
    );
    const kbs = ta?.keyBehaviours;
    return Array.isArray(kbs) ? kbs : [];
}

/**
 * Options for Scoring component UI: `{ value, label }[]` using `kbCode` / `kbName`.
 * Falls back to behaviour + knowledge when the area is unknown or has no behaviours.
 */
export function scoringComponentSelectOptions(catalog, areaName) {
    const kbs = keyBehavioursForThreatAreaName(catalog, areaName);
    if (!kbs.length) {
        return DEFAULT_SCORING_OPTIONS.map((o) => ({ ...o }));
    }
    return kbs.map((k) => {
        const rawCode = String(k?.kbCode ?? '').trim();
        const code = (rawCode || 'behaviour').toLowerCase();
        const name =
            (k?.kbName && String(k.kbName).trim()) || rawCode || code;
        const bracketCode = rawCode || code;
        return {
            value: code,
            label: formatScoringOptionLabel(bracketCode, name),
        };
    });
}

/** First comma-separated token of `components`, lowercased. */
export function primaryComponentToken(components) {
    const first = String(components ?? 'behaviour')
        .split(',')[0]
        .trim()
        .toLowerCase();
    return first || 'behaviour';
}

/**
 * Value for the question-level "Scoring component" select.
 */
export function questionComponentsSelectValue(components, options) {
    const token = primaryComponentToken(components);
    if (!Array.isArray(options) || !options.length) {
        return token === 'knowledge' ? 'knowledge' : 'behaviour';
    }
    const hit = options.find(
        (o) => String(o.value).toLowerCase() === token
    );
    return hit ? hit.value : options[0].value;
}
